'use client';

import { useId } from 'react';
import { BlockRoot } from '@akademi/site-builder-runtime';
import { Icon } from '../shared/Icon';
import {
  childLevel,
  headingLevel,
  items,
  SkImage,
  SkLink,
  surfaceClass,
  text,
  type HeadingLevel,
  type Localized,
  type SkImageRef,
  type Surface,
} from '../shared/primitives';
import { SkBaseStyles, SkStyles } from '../styles/SkStyles';

/**
 * Inspiration: three cards (for example gallery, guides, real projects).
 *
 * <p>The middle card's image takes the leaf mask so the row echoes the feature
 * rows above it without repeating the same shape three times. One column on a
 * phone, three from 769 px.
 */
export interface SpatialKitchenInspirationCard {
  label?: Localized;
  title?: Localized;
  body?: Localized;
  href?: string;
  linkLabel?: Localized;
  image?: SkImageRef;
}

export interface SpatialKitchenInspirationProps {
  eyebrow?: Localized;
  title?: Localized;
  intro?: Localized;
  cards?: SpatialKitchenInspirationCard[];
  surface?: Surface;
  headingLevel?: HeadingLevel;
}

const CSS = `
.sk-insp__grid{display:grid;gap:2.5rem}
@media (min-width:769px){.sk-insp__grid{grid-template-columns:repeat(3,minmax(0,1fr));gap:clamp(1.25rem,2.25vw,2.25rem)}}
.sk-insp__card{display:grid;gap:1rem;align-content:start}
.sk-insp__media{aspect-ratio:5/4;border-radius:8px}
.sk-insp__card:nth-child(2) .sk-insp__media{border-radius:var(--sk-leaf)}
.sk-insp__label{justify-self:start}
.sk-insp__title{font-size:clamp(1.375rem,1.2rem + .6vw,1.75rem)}
`;

export function SpatialKitchenInspiration(props: SpatialKitchenInspirationProps) {
  const cards = items<SpatialKitchenInspirationCard>(props.cards).slice(0, 3);
  const title = text(props.title, 'Inspiration');
  const level = headingLevel(props.headingLevel, 'h2');
  const Title = level;
  const CardTitle = childLevel(level);
  const titleId = useId();

  return (
    <BlockRoot
      as="section"
      className={`sk sk-insp sk-section ${surfaceClass(props.surface, 'white')}`}
      aria-labelledby={titleId}
    >
      <SkBaseStyles />
      <SkStyles id="inspiration" css={CSS} />
      <div className="sk-container">
        <div className="sk-section-head">
          {text(props.eyebrow) && <p className="sk-eyebrow">{text(props.eyebrow)}</p>}
          <Title id={titleId} className="sk-heading sk-h2">
            {title}
          </Title>
          {text(props.intro) && <p className="sk-lead">{text(props.intro)}</p>}
        </div>
        <ul className="sk-insp__grid sk-list-reset">
          {cards.map((card, index) => {
            const cardTitle = text(card.title);
            const linkLabel = text(card.linkLabel, 'Read more');
            return (
              <li key={`${cardTitle}-${index}`} className="sk-insp__card">
                <div className="sk-insp__media sk-media">
                  <SkImage image={card.image} sizes="(min-width: 769px) 33vw, 100vw" />
                </div>
                {text(card.label) && <span className="sk-pill sk-insp__label">{text(card.label)}</span>}
                <CardTitle className="sk-heading sk-insp__title">{cardTitle}</CardTitle>
                {text(card.body) && <p className="sk-body">{text(card.body)}</p>}
                {card.href && (
                  <p style={{ margin: 0 }}>
                    <SkLink href={card.href} className="sk-textlink" aria-label={`${linkLabel}: ${cardTitle}`}>
                      {linkLabel}
                      <Icon name="arrow-right" size={16} />
                    </SkLink>
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </BlockRoot>
  );
}
